import { flux } from '@/core/flux.module';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import _ from 'lodash';
import { extractErrorMessage, fetchStatsSummaryData } from '@/summaryReport/summaryReport.utilities';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { setTrendItemColor } from '@/trendData/trend.actions';
import type { X_AXIS_LABEL, Y_AXIS_LABEL } from '@/summaryReport/summaryData.constants';
import { sqSummaryReportStore } from '@/core/core.stores';
import { sqFormulasApi } from '@/sdk/api/FormulasApi';
import { errorToast } from '@/utilities/toast.utilities';

export interface ItemSignalAndCondition {
  signal?: any;
  condition?: any;
}

export function setItems(items: StatisticsSummaryItem[]): void {
  flux.dispatch('SR_SET_ITEMS', { items });
}

async function fetchAndBuildItemPayload(item: StatisticsSummaryItem): Promise<StatisticsSummaryItem> {
  const result = await fetchStatsSummaryData(item.id);
  return { ...item, ...result, error: undefined };
}

export async function addStatsSummary(item: StatisticsSummaryItem): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      flux.dispatch('SR_ADD_ITEM', { item: payload });
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      flux.dispatch('SR_ADD_ITEM', { item: { ...item, error: { message: errorMessage } } });
    })
    .finally(() => {
      setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function insertStatsSummaryAtIndex(item: StatisticsSummaryItem, index: number): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      flux.dispatch('SR_INSERT_ITEM_AT', { item: payload, index });
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      flux.dispatch('SR_INSERT_ITEM_AT', { item: { ...item, error: { message: errorMessage } }, index });
    })
    .finally(() => {
      setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function refreshStatsData(id: string): Promise<void> {
  const item = sqSummaryReportStore.findItem(id);
  if (!item) {
    return;
  }
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      flux.dispatch('SR_UPDATE_ITEM', { item: payload });
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      flux.dispatch('SR_UPDATE_ITEM', { item: { ...item, error: { message: errorMessage } } });
    })
    .finally(() => {
      setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function fetchItemsSignalAndConditionMap(
  items: StatisticsSummaryItem[],
): Promise<Record<string, ItemSignalAndCondition>> {
  const signalAndConditionMap: Record<string, ItemSignalAndCondition> = {};

  await Promise.all(
    items.map(async (item: StatisticsSummaryItem) => {
      signalAndConditionMap[item.id] = await fetchItemSignalAndCondition(item.id);
    }),
  );

  return signalAndConditionMap;
}

async function fetchItemSignalAndCondition(itemId: string): Promise<ItemSignalAndCondition> {
  try {
    const { data } = await sqFormulasApi.getItem({ id: itemId });
    const signal = _.find(data.parameters, (param) => param.name === 'signal')?.item as any;
    const condition = _.find(data.parameters, (param) => param.name === 'condition')?.item as any;
    return { signal, condition };
  } catch (error) {
    // TODO CRAB-53442 - Add improved and consistent error and redaction handling
    errorToast({ httpResponseOrError: error });
    return { signal: undefined, condition: undefined };
  }
}

export function setBaseline(item: StatisticsSummaryItem | null): void {
  flux.dispatch('SR_SET_BASELINE', { item });
}

export function removeItems(items: StatisticsSummaryItem[]): void {
  flux.dispatch('SR_REMOVE_ITEMS', { items });
}

export function setIsItemsOverlaid(isItemsOverlaid: boolean): void {
  flux.dispatch('SR_SET_IS_ITEMS_OVERLAID', { isItemsOverlaid });
}

export function setStatus(status: ITEM_DATA_STATUS): void {
  flux.dispatch('SR_SET_STATUS', { status });
}

export function setShowHistogram(showHistogram: boolean): void {
  flux.dispatch('SR_SET_SHOW_HISTOGRAM', { showHistogram });
}

export function setShowBoxPlot(showBoxPlot: boolean): void {
  flux.dispatch('SR_SET_SHOW_BOX_PLOT', { showBoxPlot });
}

export function setShowTable(showTable: boolean): void {
  flux.dispatch('SR_SET_SHOW_TABLE', { showTable });
}

export function setVisibleStats(visibleStats: string[]): void {
  visibleStats.sort();
  flux.dispatch('SR_SET_VISIBLE_STATS', { visibleStats });
}

export function setShowBellCurve(showBellCurve: boolean): void {
  flux.dispatch('SR_SET_SHOW_BELL_CURVE', { showBellCurve });
}

export function setXAxisLabel(xAxisLabel: X_AXIS_LABEL): void {
  flux.dispatch('SR_SET_X_AXIS_LABEL', { xAxisLabel });
}

export function setYAxisLabel(yAxisLabel: Y_AXIS_LABEL): void {
  flux.dispatch('SR_SET_Y_AXIS_LABEL', { yAxisLabel });
}

export function setShowSignal(showSignal: boolean): void {
  flux.dispatch('SR_SET_SHOW_SIGNAL', { showSignal });
}

export function setShowTimeRange(showTimeRange: boolean): void {
  flux.dispatch('SR_SET_SHOW_TIME_RANGE', { showTimeRange });
}

export function setShowCondition(showCondition: boolean): void {
  flux.dispatch('SR_SET_SHOW_CONDITION', { showCondition });
}

export function setShowBoxplotPoints(showBoxplotPoints: boolean): void {
  flux.dispatch('SR_SET_SHOW_BOXPLOT_POINTS', { showBoxplotPoints });
}

export async function fetchStatsData(items: StatisticsSummaryItem[]): Promise<void> {
  setStatus(ITEM_DATA_STATUS.LOADING);
  try {
    const updatedItems = await Promise.all(
      _.map(items, (item) =>
        fetchStatsSummaryData(item.id)
          .then((result) => ({ ...item, ...result, error: undefined }))
          .catch((error) => {
            const errorMessage = extractErrorMessage(error);
            return { ...item, error: { message: errorMessage } };
          }),
      ),
    );
    setItems(updatedItems);
  } finally {
    setStatus(ITEM_DATA_STATUS.PRESENT);
  }
}
